import { useSubjectLanguageVariants } from '@features/user';
import { Box, Typography } from '@mui/material';

import { ParticipantTile } from './ParticipantTile';

interface VideoSpotlightProps {
  remoteParticipantIds: string[];
  localSessionId: string;
  /** Active speaker id from `useCallLayout`. Used to pick which remote is in the spotlight in 3+ calls; falls back to `remoteParticipantIds[0]` when absent. */
  activeSpeakerId?: string | null;
  /** When true, stretches to fill available height instead of using 16:9 aspect ratio */
  fillHeight?: boolean;
  /** When false, hides the local participant thumbnail. Defaults to true. */
  showLocalThumbnail?: boolean;
}

export function VideoSpotlight({
  remoteParticipantIds,
  localSessionId,
  activeSpeakerId,
  fillHeight = false,
  showLocalThumbnail = true,
}: VideoSpotlightProps) {
  const { singular } = useSubjectLanguageVariants();
  const remoteId = activeSpeakerId ?? remoteParticipantIds[0];

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        ...(fillHeight ? { flex: 1, minHeight: 0, height: '100%' } : { aspectRatio: '16 / 9' }),
        borderRadius: '8px',
        bgcolor: 'grey.900',
        overflow: 'hidden',
      }}
    >
      {/* Remote participant (fullscreen) */}
      {remoteId ? (
        <ParticipantTile sessionId={remoteId} variant="main" />
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography variant="body1" sx={{ color: 'grey.200' }}>
            {`Waiting for ${singular} to join...`}
          </Typography>
        </Box>
      )}

      {/* Local participant (PIP thumbnail) */}
      {showLocalThumbnail && localSessionId && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            width: '24%',
            minWidth: 160,
            borderRadius: 2,
            overflow: 'hidden',
            border: '2px solid',
            borderColor: 'common.white',
          }}
        >
          <ParticipantTile sessionId={localSessionId} variant="thumbnail" isLocal />
        </Box>
      )}
    </Box>
  );
}
